import {
  Box,
  Button,
  ClickAwayListener,
  makeStyles,
  Paper,
  Popper,
  PopperPlacementType,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InputCheckbox from '../../../../../../../components/Inputs/Checkbox';
import InputSwitch from '../../../../../../../components/Inputs/Switch';
import { SwitchSize } from '../../../../../../../components/Inputs/Switch/Switch';
import { BorderWidthWithColor, FontSizes } from '../../../../../../../core/config/mainTheme';
import { toCssValue } from '../../../../../../../core/utils/theme.utils';
import {
  useOwnershipConnectionsDispatch,
  useOwnershipConnectionsState,
} from '../../OwnershipConnectionsContext/OwnershipConnectionsContext';
import { OwnershipConnectionsContextActionTypes } from '../../OwnershipConnectionsContext/OwnershipConnectionsContext.types';
import ConnectionsLegend from './ConnectionsLegend';

export interface ActionBoxProps {
  buttonTitle?: string;
  placement?: PopperPlacementType;
}

const FormActionBox: React.FC<ActionBoxProps> = ({ buttonTitle, placement = 'bottom-end' }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const dispatch = useOwnershipConnectionsDispatch();
  const {
    onlyBeneficialOwnership,
    includeHistoricalOwnership,
    showAllConnections,
  } = useOwnershipConnectionsState();

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  return (
    <Box className={`${classes.rightBox}`}>
      <Button
        className={classes.button}
        aria-controls="customized-menu"
        ref={anchorRef}
        aria-haspopup="true"
        variant="contained"
        color="primary"
        onClick={handleClick}
      >
        {buttonTitle}
        <ArrowDropDownIcon />
      </Button>
      <ClickAwayListener onClickAway={handleClose}>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement={placement}
          className={classes.menuBox}
        >
          <Paper className={classes.dropContainer}>
            <Box className={classes.rightBox}>
              <Box width={140}>{t(`connections:form.showShareholders`)}</Box>
              <InputSwitch
                fieldName={t(`connections:form.showBeneficial`)}
                formControlClassName={classes.itemSwitch}
                size={SwitchSize.small}
                checked={onlyBeneficialOwnership}
                onChange={(value) => {
                  dispatch({
                    type: OwnershipConnectionsContextActionTypes.SetOnlyBeneficialOwnership,
                    payload: value,
                  });
                }}
              />
              <Box width={140}>{t(`connections:form.showBeneficial`)}</Box>
            </Box>
            <Box className={classes.formBox}>
              <InputCheckbox
                label={t('connections:form:showCurrentOwnership')}
                name="includeHistoricalOwnership"
                size="small"
                defaultValue={includeHistoricalOwnership}
                colorCheckbox="secondary"
                onChange={(value) => {
                  dispatch({
                    type: OwnershipConnectionsContextActionTypes.SetIncludeHistoricalOwnership,
                    payload: value,
                  });
                }}
              />
              <InputCheckbox
                label={t('connections:form:showAllConnections')}
                name="showAllConnections"
                size="small"
                defaultValue={showAllConnections}
                colorCheckbox="secondary"
                onChange={(value) => {
                  dispatch({
                    type: OwnershipConnectionsContextActionTypes.SetShowAllConnections,
                    payload: value,
                  });
                }}
              />
            </Box>
            <ConnectionsLegend />
          </Paper>
        </Popper>
      </ClickAwayListener>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  menuBox: {
    zIndex: 2,
  },
  dropContainer: {
    background: theme.palette.primary.dark,
    color: theme.palette.background.paper,
    borderTop: BorderWidthWithColor(1, theme.palette.primary.contrastText),
    borderRadius: 0,
    padding: toCssValue([theme.spacing(8), theme.spacing(12)]),
  },
  rightBox: {
    alignItems: 'center',
    fontSize: FontSizes.connectionFormSize,
    marginLeft: 'auto',
    position: 'relative',
    display: 'flex',
    background: theme.palette.primary.dark,
    '& button': {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
    },
  },
  button: {
    background: theme.palette.primary.dark,
    borderRight: `1px solid ${theme.palette.background.paper}`,
    borderLeft: `1px solid ${theme.palette.background.paper}`,
    borderRadius: 0,
    '& svg': {
      marginLeft: '10px',
    },
  },
  itemSwitch: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '20%',
    '& .MuiSwitch-track': {
      backgroundColor: '#FFF',
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#FFF',
    },
    '& .MuiSwitch-thumb': {
      color: '#FFF',
    },
  },
  formBox: {
    '& .MuiTypography-root': {
      fontSize: FontSizes.connectionFormSize,
    },
    '& .MuiCheckbox-root': {
      padding: '4px',
      '&.Mui-checked': {
        '& .MuiIconButton-label': {
          '&:after': {
            position: 'absolute',
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            zIndex: -1,
          },
        },
      },
      '& .MuiIconButton-label': {
        position: 'relative',
        zIndex: 1,
        color: theme.palette.secondary.main,
      },
    },
  },
}));

export default FormActionBox;
